import React, { useState, useEffect, useRef } from 'react';

function AnimatedBlobBackground({ sections = [] }) {
  const [orangeBlobPosition, setOrangeBlobPosition] = useState({
    x: 20,
    y: 30,
  });
  const [whiteBlobPosition, setWhiteBlobPosition] = useState({ x: 70, y: 60 });
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeRef = useRef(0);

  // Floating animation when not scrolling
  useEffect(() => {
    if (isScrolling) return;

    const animate = () => {
      timeRef.current += 0.01;
      const time = timeRef.current;

      const orangeX = 20 + Math.sin(time * 0.5) * 15 + Math.cos(time * 0.3) * 10;
      const orangeY = 30 + Math.cos(time * 0.4) * 20 + Math.sin(time * 0.6) * 8;

      const whiteX = 70 + Math.sin(time * 0.6) * 12 + Math.cos(time * 0.4) * 15;
      const whiteY = 60 + Math.cos(time * 0.5) * 18 + Math.sin(time * 0.3) * 10;

      setOrangeBlobPosition({ x: orangeX, y: orangeY });
      setWhiteBlobPosition({ x: whiteX, y: whiteY });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isScrolling]);

  // Handle scroll-based positioning
  useEffect(() => {
    let currentSection = null;

    const handleScroll = () => {
      setIsScrolling(true);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Check section positions
      if (sections.length > 0) {
        const scrollPosition = window.scrollY + window.innerHeight / 2;
        let foundSection = null;

        // Find the current section in viewport
        for (const section of sections) {
          const element = document.querySelector(section.selector);
          if (!element) continue;

          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementBottom = elementTop + rect.height;

          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            foundSection = section;
            break;
          }
        }

        // Only update if moved to a different section
        if (foundSection && foundSection !== currentSection) {
          currentSection = foundSection;
          const element = document.querySelector(foundSection.selector);
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const viewportPercentage =
            ((elementTop + rect.height / 2) / document.body.scrollHeight) * 100;

          // Move blobs based on section configuration
          if (foundSection.imagePosition === 'left') {
            setOrangeBlobPosition({ x: 15, y: viewportPercentage });
            setWhiteBlobPosition({ x: 85, y: viewportPercentage + 5 });
          } else if (foundSection.imagePosition === 'right') {
            setOrangeBlobPosition({ x: 85, y: viewportPercentage });
            setWhiteBlobPosition({ x: 15, y: viewportPercentage + 5 });
          } else if (foundSection.imagePosition === 'center') {
            setOrangeBlobPosition({ x: 30, y: viewportPercentage });
            setWhiteBlobPosition({ x: 70, y: viewportPercentage + 5 });
          }
        }
      }

      // Resume floating after scroll stops
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        currentSection = null;
      }, 150);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [sections]);

  return (
    <div className="fixed inset-0 pointer-events-none -z-10 overflow-hidden">
      {/* Orange Blob */}
      <div
        className="absolute w-[32rem] h-[32rem] rounded-full opacity-30 blur-3xl transition-all duration-1000 ease-out"
        style={{
          left: `${orangeBlobPosition.x}%`,
          top: `${orangeBlobPosition.y}%`,
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle, rgba(246, 145, 29, 0.4) 0%, rgba(229, 64, 4, 0.4) 50%, transparent 100%)',
        }}
      />

      {/* White Blob */}
      <div
        className="absolute w-[32rem] h-[32rem] rounded-full opacity-20 blur-3xl transition-all duration-1000 ease-out"
        style={{
          left: `${whiteBlobPosition.x}%`,
          top: `${whiteBlobPosition.y}%`,
          transform: 'translate(-50%, -50%)',
          background:
            'radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)',
        }}
      />
    </div>
  );
}

export default AnimatedBlobBackground;
